import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { Sparkles, Upload, PenLine, CheckCircle2, Loader2 } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { waShareUrl } from "../../lib/api";
import { inr } from "./mockData";

const MOCK_PARSE = { amount: 6000, date: "14 Jun 2026", utr: "512044771236", payer: "LAKSHMI D", app: "PhonePe" };

export function RecordPaymentDialog({ open, onClose, flats, defaultFlatId, onRecorded, residentMode = false }) {
  const [step, setStep] = useState("method");
  const [parsing, setParsing] = useState(false);
  const [flatId, setFlatId] = useState(defaultFlatId || "");
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("UPI");
  const [receiptNo, setReceiptNo] = useState(null);

  const flat = flats.find((f) => f.id === (flatId || defaultFlatId));

  function reset() { setStep("method"); setParsing(false); setAmount(""); setReceiptNo(null); setMode("UPI"); }
  function close() { reset(); onClose(); }

  function startAiParse() {
    setStep("ai"); setParsing(true);
    setTimeout(() => {
      setParsing(false);
      setAmount(String(flat ? flat.dueNow || MOCK_PARSE.amount : MOCK_PARSE.amount));
      if (!flatId) setFlatId(defaultFlatId || "f4");
    }, 1800);
  }

  function confirm() {
    const no = "RCP-2026-0" + (40 + Math.floor(Math.random() * 50));
    setReceiptNo(no);
    setStep("done");
    onRecorded?.(flatId || defaultFlatId, residentMode);
    toast.success(residentMode ? "Payment reported — treasurer will confirm" : `Payment recorded · Receipt ${no}`);
  }

  const shareText = `🧾 Receipt ${receiptNo} — Sunrise Residency\nFlat ${flat?.flat} · ${inr(amount || 0)} received via ${mode}.\nThank you! — via NivasOne`;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="record-payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display">
            {residentMode ? "Report my payment" : "Record a payment"}
          </DialogTitle>
        </DialogHeader>

        {step === "method" && (
          <div className="space-y-3">
            {flat && <div className="text-sm text-stone-500">Flat <b className="text-stone-800">{flat.flat} · {flat.name}</b> — due {inr(flat.dueNow + (flat.arrears || 0))}</div>}
            <button onClick={startAiParse} data-testid="pay-method-screenshot"
              className="w-full flex items-center gap-3 p-4 rounded-2xl border-2 border-[#C05640]/30 bg-[#C05640]/5 hover:border-[#C05640] text-left transition">
              <span className="w-10 h-10 rounded-xl bg-[#C05640] text-white grid place-items-center shrink-0"><Upload size={18} /></span>
              <span>
                <span className="block text-sm font-semibold">Upload payment screenshot / SMS</span>
                <span className="block text-xs text-stone-500 flex items-center gap-1 mt-0.5"><Sparkles size={12} className="text-[#C05640]" /> AI reads amount, date & UTR for you</span>
              </span>
            </button>
            <button onClick={() => setStep("manual")} data-testid="pay-method-manual"
              className="w-full flex items-center gap-3 p-4 rounded-2xl border border-stone-200 hover:border-stone-400 text-left transition">
              <span className="w-10 h-10 rounded-xl bg-stone-100 text-stone-600 grid place-items-center shrink-0"><PenLine size={18} /></span>
              <span>
                <span className="block text-sm font-semibold">Enter manually</span>
                <span className="block text-xs text-stone-500 mt-0.5">Cash, cheque or bank transfer details</span>
              </span>
            </button>
          </div>
        )}

        {step === "ai" && parsing && (
          <div className="py-10 text-center" data-testid="ai-parsing-state">
            <Loader2 className="mx-auto animate-spin text-[#C05640]" size={32} />
            <div className="mt-3 text-sm font-medium">Reading screenshot with AI…</div>
            <div className="text-xs text-stone-500 mt-1">Extracting amount, date, UTR & payer name</div>
          </div>
        )}

        {step === "ai" && !parsing && (
          <div className="space-y-3" data-testid="ai-parsed-result">
            <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-3 text-sm">
              <div className="flex items-center gap-1.5 text-emerald-700 font-semibold text-xs mb-2"><Sparkles size={13} /> AI extracted — please verify</div>
              <div className="grid grid-cols-2 gap-y-1.5 text-xs">
                <span className="text-stone-500">Amount</span><b>{inr(amount || MOCK_PARSE.amount)}</b>
                <span className="text-stone-500">Date</span><b>{MOCK_PARSE.date}</b>
                <span className="text-stone-500">UTR / Ref</span><b>{MOCK_PARSE.utr}</b>
                <span className="text-stone-500">Payer</span><b>{MOCK_PARSE.payer} · {MOCK_PARSE.app}</b>
              </div>
            </div>
            {!residentMode && (
              <div>
                <label className="text-xs font-medium text-stone-600">Match to flat</label>
                <select value={flatId || defaultFlatId || ""} onChange={(e) => setFlatId(e.target.value)} data-testid="ai-flat-select"
                  className="mt-1 w-full rounded-xl border border-stone-200 p-2.5 text-sm bg-white">
                  {flats.map((f) => <option key={f.id} value={f.id}>{f.flat} · {f.name}</option>)}
                </select>
                <div className="text-[11px] text-[#C05640] mt-1 flex items-center gap-1"><Sparkles size={11} /> Suggested: {flat?.flat} (name matches payer)</div>
              </div>
            )}
            <div className="text-xs text-stone-500">Will be allocated to: <b className="text-stone-800">oldest pending dues first</b></div>
            <Button onClick={confirm} data-testid="ai-confirm-btn" className="w-full bg-[#C05640] hover:bg-[#A8492F] rounded-full">
              {residentMode ? "Submit for treasurer confirmation" : "Confirm & generate receipt"}
            </Button>
          </div>
        )}

        {step === "manual" && (
          <div className="space-y-3" data-testid="manual-entry-form">
            {!residentMode && (
              <div>
                <label className="text-xs font-medium text-stone-600">Flat</label>
                <select value={flatId || defaultFlatId || ""} onChange={(e) => setFlatId(e.target.value)} data-testid="manual-flat-select"
                  className="mt-1 w-full rounded-xl border border-stone-200 p-2.5 text-sm bg-white">
                  <option value="">Select flat…</option>
                  {flats.map((f) => <option key={f.id} value={f.id}>{f.flat} · {f.name}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-stone-600">Amount (₹)</label>
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="2000" data-testid="manual-amount-input" className="mt-1 rounded-xl" />
            </div>
            <div>
              <label className="text-xs font-medium text-stone-600">Mode</label>
              <div className="mt-1 flex gap-2">
                {["UPI", "Bank", "Cash", "Cheque"].map((m) => (
                  <button key={m} onClick={() => setMode(m)} data-testid={`mode-${m.toLowerCase()}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${mode === m ? "bg-stone-900 text-white border-stone-900" : "border-stone-200 text-stone-600 hover:border-stone-400"}`}>
                    {m}
                  </button>
                ))}
              </div>
            </div>
            <Button onClick={confirm} disabled={!amount || !(flatId || defaultFlatId)} data-testid="manual-confirm-btn"
              className="w-full bg-[#C05640] hover:bg-[#A8492F] rounded-full">
              {residentMode ? "Submit for treasurer confirmation" : "Record & generate receipt"}
            </Button>
          </div>
        )}

        {step === "done" && (
          <div className="text-center py-4" data-testid="payment-done-state">
            <CheckCircle2 className="mx-auto text-emerald-500" size={44} />
            {residentMode ? (
              <>
                <div className="mt-3 font-semibold">Payment reported!</div>
                <div className="text-sm text-stone-500 mt-1">The treasurer will confirm it shortly. You'll get your receipt once confirmed.</div>
              </>
            ) : (
              <>
                <div className="mt-3 font-semibold">Receipt {receiptNo} generated</div>
                <div className="text-sm text-stone-500 mt-1">Flat {flat?.flat} · {inr(amount || 0)} via {mode}</div>
                <Button onClick={() => window.open(waShareUrl(shareText), "_blank")} data-testid="receipt-wa-share"
                  className="mt-4 bg-[#25D366] hover:bg-[#1EBE55] text-white rounded-full">
                  <FaWhatsapp className="mr-2" size={16} /> Share receipt on WhatsApp
                </Button>
              </>
            )}
            <Button variant="ghost" onClick={close} data-testid="payment-done-close" className="mt-2 w-full text-stone-500">Close</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
